import React, { useEffect, useRef, useState } from 'react';
import { Card, Button, message, Modal, Spin, Typography, Row, Col, Space, Tag } from 'antd';
import { PrinterOutlined, ArrowLeftOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { getCaseSummaryApi, deleteCaseSummaryApi } from '../../../api/master.api';

const { Title, Text } = Typography;

const CaseSummaryView = () => {
    const { case_id } = useParams();
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        loadReportData();
    }, [case_id]);

    const loadReportData = async () => {
        setLoading(true);
        try {
            const response = await getCaseSummaryApi({ caseId: case_id });
            if (response.data.success) {
                const data = response.data.data;
                setReportData(Array.isArray(data) ? data[0] : data);
            } else {
                message.error("ডাটা লোড করতে সমস্যা হয়েছে");
            }
        } catch (error) {
            console.error('Error loading cases:', error);
            message.error("রিপোর্ট লোড করতে ব্যর্থ হয়েছে");
        } finally {
            setLoading(false);
        }
    };

    const componentRef = useRef(null);
    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        documentTitle: `Case_Summary_${reportData?.month || ''}_${reportData?.year || ''}`,
    });

    const handleEdit = () => {
        navigate(`/case-summary-edit/${case_id}`);
    };

    const handleDelete = async () => {
        Modal.confirm({
            title: 'আপনি কি নিশ্চিত?',
            content: 'এই কেস সারাংশটি মুছে ফেলা হবে। এই কাজটি পুনরায় করা যাবে না।',
            okText: 'হ্যাঁ, মুছুন',
            okType: 'danger',
            cancelText: 'না, বাতিল করুন',
            onOk: async () => {
                try {
                    await deleteCaseSummaryApi(case_id);
                    message.success("রিপোর্ট সফলভাবে মুছে ফেলা হয়েছে");
                    navigate('/case-summary');
                } catch (error) {
                    console.error('Error deleting case:', error);
                    message.error("মুছে ফেলা ব্যর্থ হয়েছে");
                }
            }
        });
    };

    const handleBack = () => {
        navigate('/case-summary');
    };

    // Helper function to get value from reportData
    const getValue = (key, defaultValue = '-') => {
        if (!reportData) return defaultValue;
        return reportData[key] || reportData[key.toUpperCase()] || reportData[key.toLowerCase()] || defaultValue;
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" tip="লোড হচ্ছে..." />
            </div>
        );
    }

    if (!reportData) {
        return (
            <div style={{ padding: '50px', textAlign: 'center' }}>
                <Title level={4}>রিপোর্ট পাওয়া যায়নি</Title>
                <Button onClick={handleBack} type="primary" style={{ marginTop: 20 }}>
                    ফিরে যান
                </Button>
            </div>
        );
    }

    return (
        <div style={{ background: "#f0f2f5", minHeight: "100vh", padding: "24px" }}>
            <div style={{ maxWidth: 1400, margin: "0 auto" }}>
                {/* Header with Actions */}
                <Card style={{ marginBottom: 24, borderRadius: 12 }}>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Space>
                                <Button
                                    icon={<ArrowLeftOutlined />}
                                    onClick={handleBack}
                                    size="large"
                                >
                                    ফিরে যান
                                </Button>
                                <Title level={4} style={{ margin: 0, color: "#1a5f7a" }}>
                                    মামলা সংক্রান্ত তথ্য বিবরণী
                                </Title>
                            </Space>
                        </Col>
                        <Col>
                            <Space>
                                <Tag color="blue">
                                    {getValue('month')} {getValue('year')}
                                </Tag>

                                <Button
                                    type="primary"
                                    icon={<PrinterOutlined />}
                                    onClick={handlePrint}
                                    size="large"
                                >
                                    প্রিন্ট
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>

                {/* Report Content */}
                <div ref={componentRef}>
                    <Card style={{ borderRadius: 12, overflowX: 'auto' }}>
                        {/* Header */}
                        <div style={{ textAlign: 'center', marginBottom: 30, padding: 20, borderBottom: '2px solid #1a5f7a' }}>
                            <Title level={4} style={{ color: '#1a5f7a' }}>
                                বাংলাদেশ সেতু কর্তৃপক্ষ এবং এর আওতাধীন প্রকল্পসমূহের মামলা সংক্রান্ত তথ্যঃ
                            </Title>
                        </div>

                        {/* Section 1: Supreme Court */}
                        <div style={{ marginBottom: 30 }}>
                            <Title level={5} style={{ marginBottom: 15 }}>
                                * উচ্চ আদালতে (সুপ্রীম কোর্ট) চলমান মামলার তালিকাঃ
                            </Title>

                            <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', fontSize: 12 }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#fafafa' }}>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '6%' }} rowSpan={2}>ক্রম</th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '20%' }} rowSpan={2}>বিবরণ</th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '15%' }} rowSpan={2}>
                                            {getValue('month')} {getValue('year')} মাসে দায়েরকৃত মামলা
                                        </th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '15%' }} rowSpan={2}>
                                            {getValue('date1')} পর্যন্ত অনিষ্পন্ন মামলা
                                        </th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '16%' }} colSpan={2}>
                                            {getValue('date2')} মাসে নিষ্পত্তিকৃত মামলা
                                        </th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '8%' }} rowSpan={2}>চলমান মামলা</th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '20%' }} rowSpan={2}>মন্তব্য</th>
                                    </tr>
                                    <tr style={{ backgroundColor: '#fafafa' }}>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '8%' }}>পক্ষে</th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '8%' }}>বিপক্ষে</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(reportData.section1Data || reportData.SECTION1_DATA || []).map((row, idx) => (
                                        <tr key={idx}>
                                            <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>{row.serial || row.SERIAL}</td>
                                            <td style={{ border: '1px solid #000', padding: 8 }}>{row.division || row.DIVISION}</td>
                                            <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>{row.col1 || row.COL1}</td>
                                            <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>{row.col2 || row.COL2}</td>
                                            <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>{row.col3 || row.COL3}</td>
                                            <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>{row.col4 || row.COL4}</td>
                                            <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>{row.col5 || row.COL5}</td>
                                            <td style={{ border: '1px solid #000', padding: 8 }}>{row.remark || row.REMARK}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan={8} style={{ border: '1px solid #000', padding: 8, textAlign: 'center', fontWeight: 'bold' }}>
                                            উচ্চ আদালতে চলমান মামলার মোট সংখ্যা = {getValue('section1Total', getValue('SECTION1_TOTAL', '0'))}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        {/* Section 2: Arbitral Tribunal */}
                        <div style={{ marginBottom: 30 }}>
                            <Title level={5} style={{ marginBottom: 15 }}>
                                * THREE-MEMBER ARBITRAL TRIBUNAL, DHAKA (Under the Arbitration Act, 2001)
                            </Title>

                            <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', fontSize: 12 }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#fafafa' }}>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '6%' }} rowSpan={2}>ক্রম</th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '20%' }} rowSpan={2}>বিবরণ</th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '15%' }} rowSpan={2}>
                                            {getValue('month')} {getValue('year')} মাসে দায়েরকৃত কেস
                                        </th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '15%' }} rowSpan={2}>
                                            {getValue('date1')} পর্যন্ত অনিষ্পন্ন কেস
                                        </th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '16%' }} colSpan={2}>
                                            {getValue('date2')} মাসে নিষ্পত্তিকৃত কেস
                                        </th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '8%' }} rowSpan={2}>চলমান কেস</th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '20%' }} rowSpan={2}>মন্তব্য</th>
                                    </tr>
                                    <tr style={{ backgroundColor: '#fafafa' }}>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>পক্ষে</th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>বিপক্ষে</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(reportData.section2Data || reportData.SECTION2_DATA || []).map((row, idx) => (
                                        <tr key={idx}>
                                            <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>{row.serial || row.SERIAL}</td>
                                            <td style={{ border: '1px solid #000', padding: 8 }}>{row.division || row.DIVISION}</td>
                                            <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>{row.col1 || row.COL1}</td>
                                            <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>{row.col2 || row.COL2}</td>
                                            <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>{row.col3 || row.COL3}</td>
                                            <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>{row.col4 || row.COL4}</td>
                                            <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>{row.col5 || row.COL5}</td>
                                            <td style={{ border: '1px solid #000', padding: 8 }}>{row.remark || row.REMARK}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Section 3: District Courts */}
                        <div style={{ marginBottom: 30 }}>
                            <Title level={5} style={{ marginBottom: 15 }}>
                                * জেলা জজ আদালতসহ ট্রাইব্যুনাল ও অন্যান্য অধস্তন আদালতে চলমান মামলার তালিকাঃ
                            </Title>

                            <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', fontSize: 12 }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#fafafa' }}>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '6%' }} rowSpan={2}>ক্রম</th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '20%' }} rowSpan={2}>বিবরণ</th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '10%' }} rowSpan={2}>
                                            {getValue('month')} {getValue('year')} মাসে দায়েরকৃত
                                        </th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '10%' }} rowSpan={2}>
                                            {getValue('date1')} পর্যন্ত অনিষ্পন্ন
                                        </th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '16%' }} colSpan={2}>
                                            {getValue('date2')} মাসে নিষ্পত্তিকৃত
                                        </th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '8%' }} rowSpan={2}>চলমান</th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', width: '30%' }} rowSpan={2}>মন্তব্য</th>
                                    </tr>
                                    <tr style={{ backgroundColor: '#fafafa' }}>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>পক্ষে</th>
                                        <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>বিপক্ষে</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(reportData.section3Data || reportData.SECTION3_DATA || []).map((row, idx) => (
                                        <tr key={idx}>
                                            <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center', verticalAlign: 'top' }}>{row.serial || row.SERIAL}</td>
                                            <td style={{ border: '1px solid #000', padding: 8, verticalAlign: 'top' }}>{row.division || row.DIVISION}</td>
                                            <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center', verticalAlign: 'top' }}>{row.col1 || row.COL1}</td>
                                            <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center', verticalAlign: 'top' }}>{row.col2 || row.COL2}</td>
                                            <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center', verticalAlign: 'top' }}>{row.col3 || row.COL3}</td>
                                            <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center', verticalAlign: 'top' }}>{row.col4 || row.COL4}</td>
                                            <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center', verticalAlign: 'top' }}>{row.col5 || row.COL5}</td>
                                            <td style={{ border: '1px solid #000', padding: 8, verticalAlign: 'top', whiteSpace: 'pre-line' }}>{row.remark || row.REMARK}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan={8} style={{ border: '1px solid #000', padding: 8, textAlign: 'center', fontWeight: 'bold' }}>
                                            অধস্তন আদালতে চলমান মামলার মোট সংখ্যা = {getValue('section3Total', getValue('SECTION3_TOTAL', '0'))}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        {/* Grand Total */}
                        <div style={{ marginTop: 20, padding: 15, backgroundColor: '#f0f2f5', borderRadius: 8 }}>
                            <Text strong style={{ fontSize: 16 }}>
                                * উচ্চ আদালতে এবং অধস্তন আদালতে চলমান মামলার সর্বমোট সংখ্যা: {getValue('grandTotal', getValue('GRAND_TOTAL', '0'))}
                            </Text>
                        </div>

                        {/* Signatory Information */}
                        <div style={{ marginTop: 30, textAlign: 'right' }}>
                            <div style={{ lineHeight: '1.8' }}>
                                <Text strong>{getValue('name')}</Text>
                                <br />
                                <Text>{getValue('designation')}</Text>
                                <br />
                                <Text>{getValue('department')}</Text>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Print Styles */}
                <style>{`
                    @media print {
                        body {
                            margin: 0;
                            padding: 0;
                        }
                        .print-table {
                            page-break-inside: avoid;
                        }
                        .ant-card {
                            box-shadow: none;
                            border: none;
                        }
                        .ant-btn {
                            display: none !important;
                        }
                        .print-table th,
                        .print-table td {
                            border-color: #000 !important;
                        }
                    }
                `}</style>
            </div>
        </div>
    );
};

export default CaseSummaryView;